import { ReactText } from 'react';
import { spaces, spacingCurves } from '../styles/theme/default';
import logger from './logger';
import { SpaceInterface, SpaceStyleInterface } from '../../types';

/**
 * Gets Tracking values and matched them to the corresponding data-attributes
 *
 * @param {tracking} tracking information
 * @returns Object
 */
export const getTrackingValues = ({ tracking }: any) =>
  tracking
    ? {
        'data-event-category': tracking.category,
        'data-event-action': tracking.action,
        'data-event-label': tracking.label,
      }
    : null;

/**
 * Merge given ClassNames with Tracking Class
 *
 * @param {className} string with classes
 * @param {tracking} tracking information
 * @returns String
 */
export const mergeClassNames = ({ className, tracking }: any) => {
  const classes = className ? className : '';
  const trackingClass = tracking ? 'tracked' : '';
  const classString = `${classes} ${trackingClass}`.trim();

  return classString.length > 0 ? { className: classString } : null;
};

/**
 * Resolves after a certain delay
 *
 * @param {number} timeout timeout in milliseconds
 * @returns Promise
 */
export const delay = (timeout = 0) =>
  new Promise((r) => setTimeout(r, timeout));

/**
 * Receives a space name - checks if it's a static or responsive Space and returns an Object
 *
 * @param space
 * @return Object
 */
export const getSpaceOrCurve = (space: SpaceInterface) => {
  const { component: componentSpacingCurves } = spacingCurves;

  const isStatic = space in spaces;
  const isCurve = space in componentSpacingCurves;
  const spaceType: SpaceStyleInterface = {};

  // ignore the else case for code coverage - since we don't have static / responsive spaces which have the same name.
  /* istanbul ignore else */
  if (isStatic && !isCurve) {
    spaceType.staticSpace = space;
  } else if (isCurve && !isStatic) {
    spaceType.spacingCurve = space;
  } else if (!isStatic && !isCurve) {
    logger(`Space '${space}' not found!`, 'Spacer');
  } else {
    logger(
      `Space '${space}' exists in both places - rename to unique naming`,
      'Spacer',
    );
  }

  return spaceType;
};

/**
 * Wraps the given children inside the given wrapper when the condition is true - otherwise just the children is returned
 *
 * @param condition
 * @param wrapper
 * @param children
 * @param rest
 * @constructor
 */
export const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
  ...rest
}: any) => (condition ? wrapper(children, rest) : children);

/**
 * Wraps the given children either inside wrapperTrue or wrapperFalse depending on the condition
 *
 * @param condition
 * @param wrapperTrue
 * @param wrapperFalse
 * @param children
 * @param rest
 * @constructor
 */
export const AlternativeWrapper = ({
  condition,
  wrapperTrue,
  wrapperFalse,
  children,
  ...rest
}: any) => {
  return condition ? wrapperTrue(children, rest) : wrapperFalse(children, rest);
};

/**
 * Formats the given value into a valid swiss currency string
 *
 * @param value
 */

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ch-DE', {
    style: 'currency',
    currency: 'CHF',
  }).format(value);

/**
 * Compares a given Date with the current date
 *
 * @param compareDate the date to compare with
 * @param inFuture check if date is in the future
 */

export const compareDate = (compareDate: any, inFuture: boolean) => {
  const [day, month, year] = compareDate.split('.');
  const givenDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // reset time

  if (inFuture) {
    return givenDate <= today;
  } else {
    return givenDate > today;
  }
};

/**
 * Compares two given Sets for equality
 *
 * @param a set of ReactText (string or number)
 * @param b set of ReactText (string or number)
 */

export const isSetsEqual = (a: Set<ReactText>, b: Set<ReactText>) =>
  a.size === b.size && [...Array.from(a)].every((value) => b.has(value));
